import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import BrandValueSliders from './BrandValueSliders';
import Tooltip from '@/components/platform/ui/Tooltip';
import { useAlias } from '@/lib/platform/hooks/use-alias';

interface BrandValueSectionProps {
  popularity: number;
  csr: number;
  culture: number;
  loyalty: number;
  onPopularityChange: (value: number) => void;
  onCsrChange: (value: number) => void;
  onCultureChange: (value: number) => void;
  onLoyaltyChange: (value: number) => void;
  canEdit: boolean;
  labels: {
    popularity: string;
    csr: string;
    culture: string;
    loyalty: string;
  };
  helpTexts: {
    popularity: string;
    csr: string;
    culture: string;
    loyalty: string;
  };
}

export function BrandValueSection({
  popularity,
  csr,
  culture,
  loyalty,
  onPopularityChange,
  onCsrChange,
  onCultureChange,
  onLoyaltyChange,
  canEdit,
  labels,
  helpTexts,
}: BrandValueSectionProps) {
  const { darkMode } = useDarkMode();
  const { getLabel } = useAlias();

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2
          className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} flex items-center gap-2`}
        >
          {getLabel('brand_value_title')}
          <span
            className={`rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            SUBJ
          </span>
          <Tooltip
            title="Qualitative Multiplier Reference"
            definition="Brand value factors that influence long-term sponsorship effectiveness through a value multiplier. The average score of all four indicators determines the final multiplier applied to the total quantitative value."
            assessmentCriteria={[
              'Formula: Multiplier = 0.5 + (Average Score - 1) × (1.5 ÷ 9)',
              'Average Score = (Popularity + CSR + Culture + Fans/Loyalty) ÷ 4',
              'Score range: 1-10 for each indicator',
            ]}
            scoringGuidelines={[
              {
                range: '1.0-2.0',
                multiplier: '0.50x-0.67x',
                level: 'Very Negative',
                description: 'Significant brand risk, reduces value',
              },
              {
                range: '2.1-3.0',
                multiplier: '0.67x-0.83x',
                level: 'Negative',
                description: 'Below market standards, value reduction',
              },
              {
                range: '3.1-4.0',
                multiplier: '0.83x-1.00x',
                level: 'Below Average',
                description: 'Moderate concerns, slight value impact',
              },
              {
                range: '4.1-5.0',
                multiplier: '1.00x-1.17x',
                level: 'Average',
                description: 'Market standard, neutral impact',
              },
              {
                range: '5.1-6.0',
                multiplier: '1.17x-1.33x',
                level: 'Above Average',
                description: 'Good brand alignment, value enhancement',
              },
              {
                range: '6.1-7.0',
                multiplier: '1.33x-1.50x',
                level: 'Strong',
                description: 'Strong brand synergy, significant value add',
              },
              {
                range: '7.1-8.0',
                multiplier: '1.50x-1.67x',
                level: 'Very Strong',
                description: 'Excellent brand fit, major value enhancement',
              },
              {
                range: '8.1-9.0',
                multiplier: '1.67x-1.83x',
                level: 'Outstanding',
                description: 'Exceptional brand strength, premium value add',
              },
              {
                range: '9.1-10.0',
                multiplier: '1.83x-2.00x',
                level: 'Exceptional',
                description: 'Perfect alignment, maximum value multiplier',
              },
            ]}
            wide={true}
          />
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {getLabel('brand_value_description')}
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
        <div className="sm:col-span-6">
          <BrandValueSliders
            popularity={popularity}
            csr={csr}
            culture={culture}
            loyalty={loyalty}
            onPopularityChange={onPopularityChange}
            onCsrChange={onCsrChange}
            onCultureChange={onCultureChange}
            onLoyaltyChange={onLoyaltyChange}
            darkMode={darkMode}
            labels={labels}
            showValue={true}
            showHelp={true}
            helpTexts={helpTexts}
          />
        </div>
      </div>
    </div>
  );
}
